// src/Pages/Onboarding/ZidOnboarding/EmailVerification/EmailVerification.jsx
import React, { useState, useEffect } from 'react';
import { Button, message } from 'antd';
import { InputOTP } from 'antd-input-otp';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import config from '../../../../config';

export default function EmailVerification({ initialEmail, onVerified }) {
  const { t } = useTranslation();
  const [email, setEmail]           = useState(initialEmail);
  const [editing, setEditing]       = useState(false);
  const [codeSent, setCodeSent]     = useState(false);
  const [otp, setOtp]               = useState(Array(6).fill(''));
  const [generatedCode, setGeneratedCode] = useState(null);
  const [countdown, setCountdown]   = useState(0);
  const [error, setError]           = useState('');
  const [disableBtn, setDisableBtn] = useState(false);
  const [isLoading, setIsLoading]   = useState(false);

  // countdown timer effect
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // actual OTP send logic
  const sendCode = async () => {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedCode(code);

    setCodeSent(true);
    setError('');
    setCountdown(40);

    try {
      await fetch(`${config.baseUrl}/api/user/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: code, type: 'signup' }),
      });
    } catch (e) {
      console.error('Failed to send OTP:', e);
      setError(t('zidOnboarding.emailVerification.errorSendFailed'));
      setIsLoading(false);
      setDisableBtn(false);
    } finally {
      setDisableBtn(false);
      setIsLoading(false);
    }
  };

  // uniqueness check + send trigger
  const handleEmailUniqueness = async () => {
    setDisableBtn(true);
    setIsLoading(true);

    try {
      const form = new FormData();
      form.append('email', email);
      await axios.post(`${config.apiUrl}unique-email/`, form);
      sendCode();
    } catch (err) {
      setDisableBtn(false);
      message.error(t('zidOnboarding.emailVerification.errorEmailExists'));
      setIsLoading(false);
    }
  };

  const verifyCode = () => {
    if (otp.join('') === generatedCode) {
      onVerified(email);
    } else {
      setError(t('zidOnboarding.emailVerification.errorInvalidCode'));
    }
  };

  return (
    <div className="flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">
          {t('zidOnboarding.emailVerification.title')}
        </h1>

        {!codeSent ? (
          <>
            <label className="block text-gray-700 mb-2">
              {t('zidOnboarding.emailVerification.haveYourEmail')}
            </label>
            {editing ? (
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full border-b-2 border-gray-300 focus:border-purple-600 py-2 mb-4"
              />
            ) : (
              <p className="text-gray-900 font-medium mb-4">{email}</p>
            )}
            <button
              onClick={() => setEditing(e => !e)}
              className="text-purple-600 mb-6 hover:underline"
            >
              {editing
                ? t('zidOnboarding.emailVerification.cancel')
                : t('zidOnboarding.emailVerification.change')}
            </button>
            <Button
              type="primary"
              loading={isLoading}
              block
              size="large"
              onClick={handleEmailUniqueness}
              disabled={disableBtn || countdown > 0}
              style={{ backgroundColor: '#7F28F8', borderColor: '#7F28F8' }}
            >
              {countdown > 0
                ? t('zidOnboarding.emailVerification.resendIn', { countdown })
                : t('zidOnboarding.emailVerification.sendCode')}
            </Button>
          </>
        ) : (
          <>
            <p className="text-gray-700 mb-4">
              {t('zidOnboarding.emailVerification.enterCode', { email })}
            </p>
            <InputOTP
              value={otp}
              onChange={setOtp}
              length={6}
              inputType="numeric"
              wrapperStyle={{ display: 'flex', gap: 8, justifyContent: 'center' }}
              inputStyle={{
                width: 64,
                height: 64,
                backgroundColor: '#FFF',
                border: '2px solid #D1D5DB',
                borderRadius: 12,
                fontSize: '1.5rem',
                textAlign: 'center'
              }}
            />
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
            <Button
              block
              size="large"
              onClick={verifyCode}
              className="mt-6"
              style={{ backgroundColor: '#7F28F8', borderColor: '#7F28F8', color: '#FFF' }}
            >
              {t('zidOnboarding.emailVerification.verify')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
